import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import modalStyles from '../styles/modules/Modal.module.css';
import buttonStyles from '../styles/modules/Button.module.css';
import styles from '../styles/modules/ConfirmDialog.module.css';

type ConfirmTone = 'default' | 'danger';

interface ConfirmOptions {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
}

interface ConfirmDeleteOptions {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  tone: ConfirmTone;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  confirmDelete: (options: ConfirmDeleteOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

const defaultState: ConfirmDialogState = {
  open: false,
  title: 'Confirm action',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  tone: 'default',
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialog, setDialog] = useState<ConfirmDialogState>(defaultState);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const closeDialog = useCallback((value: boolean) => {
    setDialog(prev => ({ ...prev, open: false }));
    const resolver = resolveRef.current;
    resolveRef.current = null;
    if (resolver) {
      resolver(value);
    }
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>(resolve => {
      if (resolveRef.current) {
        resolveRef.current(false);
      }
      resolveRef.current = resolve;
      setDialog({
        open: true,
        title: options.title ?? defaultState.title,
        message: options.message,
        confirmLabel: options.confirmLabel ?? defaultState.confirmLabel,
        cancelLabel: options.cancelLabel ?? defaultState.cancelLabel,
        tone: options.tone ?? defaultState.tone,
      });
    });
  }, []);

  const confirmDelete = useCallback((options: ConfirmDeleteOptions) => {
    return confirm({
      title: options.title ?? 'Delete item?',
      message: options.message,
      confirmLabel: options.confirmLabel ?? 'Delete',
      cancelLabel: options.cancelLabel ?? 'Cancel',
      tone: 'danger'
    });
  }, [confirm]);

  useEffect(() => {
    if (!dialog.open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDialog(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dialog.open, closeDialog]);

  const contextValue = useMemo(() => ({ confirm, confirmDelete }), [confirm, confirmDelete]);
  const confirmButtonClass = dialog.tone === 'danger' ? buttonStyles.danger : buttonStyles.success;

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}
      {dialog.open && (
        <div className={`${modalStyles.overlay} ${styles.overlay}`} onClick={() => closeDialog(false)}>
          <div
            className={`${modalStyles.modal} ${styles.modal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={modalStyles.header}>
              <h2 id="confirm-dialog-title" className={modalStyles.title}>{dialog.title}</h2>
            </div>
            <div className={modalStyles.body}>
              <p className={styles.message}>{dialog.message}</p>
            </div>
            <div className={modalStyles.footer}>
              <button 
                className={`${styles.cancelBtn}`} 
                onClick={() => closeDialog(false)}
              >
                {dialog.cancelLabel}
              </button>
              <button className={confirmButtonClass} onClick={() => closeDialog(true)}>
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextValue['confirm'] => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context.confirm;
};

export const useConfirmDialog = (): ConfirmContextValue => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within a ConfirmProvider');
  }
  return context;
};
